/**
 * Outlook / Entry Latency Trend component.
 *
 * "Is the team's submission lag growing over time?"
 *
 * For each progress entry, compute lag = days from reporting_date to
 * created_at. Take the last N entries (default 6), split into two halves,
 * compute mean lag for each half. If the recent half's mean is materially
 * higher than the older half's mean, the team is degrading — score drops.
 *
 * Two-half comparison instead of full linear regression: simpler, more
 * robust to small N, and the signal we care about ("are we getting worse")
 * is more naturally a "recent vs older" comparison than a slope.
 *
 * Scoring band:
 *   delta <= 0      → 100 (no degradation; lag flat or improving)
 *   delta = 5 days  → ~50 (meaningfully degrading)
 *   delta >= 14 days → 0  (severe degradation)
 *
 * Linear interpolation between those anchors.
 *
 * Lifecycle: requires at least 6 entries. Below that, signal is too noisy
 * to be meaningful — return N/A. (Outlook composite already requires
 * 30-day project age, so 6 entries is a reasonable additional floor.)
 *
 * See: DEV-846 epic, DEV-847.
 */

import type { ComponentResult, DataReliabilityInputs } from '../types';

const MIN_ENTRIES_FOR_TREND = 6;

/** Days between two ISO strings (date or timestamp). 0 for invalid. */
function daysBetween(a: string, b: string): number {
  const ma = Date.parse(a);
  const mb = Date.parse(b);
  if (!Number.isFinite(ma) || !Number.isFinite(mb)) return 0;
  return Math.round((mb - ma) / (1000 * 60 * 60 * 24));
}

/** Mean of an array; 0 for empty. */
function mean(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((s, n) => s + n, 0) / arr.length;
}

/**
 * Convert a lag-delta (days the recent half is lagging more than the older
 * half) to a 0-100 score. Linear interpolation: 0d = 100, 14d = 0.
 */
function scoreFromDelta(deltaDays: number): number {
  if (deltaDays <= 0) return 100;
  if (deltaDays >= 14) return 0;
  return Math.round(100 - (deltaDays / 14) * 100);
}

export function computeEntryLatencyTrend(
  inputs: DataReliabilityInputs,
): ComponentResult {
  if (inputs.progress_entries.length < MIN_ENTRIES_FOR_TREND) {
    return {
      key: 'entry_latency_trend',
      score: null,
      applicable: false,
      reason: `Trend needs at least ${MIN_ENTRIES_FOR_TREND} entries (project has ${inputs.progress_entries.length})`,
      explain: 'Not enough history to detect a trend.',
    };
  }

  // Sort by reporting_date ascending. Most recent N entries are the cohort
  // we analyze. We use reporting_date (not created_at) for ordering because
  // the period an entry is FOR drives the analysis — late submissions
  // shouldn't reorder against prompt submissions.
  const sorted = [...inputs.progress_entries].sort((a, b) =>
    a.reporting_date.localeCompare(b.reporting_date),
  );

  // Take the most recent 6 (or however many are available; capped above).
  const cohort = sorted.slice(-MIN_ENTRIES_FOR_TREND);

  const half = Math.floor(cohort.length / 2);
  const olderLags = cohort.slice(0, half).map((e) =>
    daysBetween(e.reporting_date, e.created_at),
  );
  const recentLags = cohort.slice(-half).map((e) =>
    daysBetween(e.reporting_date, e.created_at),
  );

  const olderMean = mean(olderLags);
  const recentMean = mean(recentLags);
  const delta = recentMean - olderMean;

  const score = scoreFromDelta(delta);

  let explain: string;
  if (delta <= 0) {
    explain = `Submission lag is steady or improving (recent ${recentMean.toFixed(1)}d vs older ${olderMean.toFixed(1)}d).`;
  } else if (delta < 3) {
    explain = `Submission lag slightly up (${delta.toFixed(1)}d slower than older entries).`;
  } else {
    explain = `Submission lag growing (recent entries ${delta.toFixed(1)}d slower than older entries).`;
  }

  return {
    key: 'entry_latency_trend',
    score,
    applicable: true,
    explain,
  };
}
